import { DATE_FORMAT_FOR_API } from '@rideiq/constants';
import { formatDateString } from '@rideiq/utils/helpers/formatDate';
import { format } from 'date-fns';

import { LocationDraft } from '../../types';

const DISPLAY_DATE_FORMAT = 'MM/dd/yyyy';

const toApiDateString = (date: string | Date): string =>
  typeof date === 'string' ? date : format(date, DATE_FORMAT_FOR_API);

const isImmediateEffectiveDate = (effectiveAt: string | Date | null | undefined): boolean => {
  if (!effectiveAt) {
    return false;
  }

  const today = format(new Date(), DATE_FORMAT_FOR_API);

  return toApiDateString(effectiveAt) === today;
};

const formatEffectiveDate = (effectiveAt: string | Date): string =>
  formatDateString(toApiDateString(effectiveAt), DISPLAY_DATE_FORMAT);

const isSameLocationId = (a: unknown, b: unknown): boolean => Number(a) === Number(b);

const LOCATION_TYPE_NOUN: Record<string, string> = {
  residence: 'residence',
  school: 'destination',
  other: 'location',
};

const getReplacementNoun = (location: LocationDraft['location']): string => {
  const noun = LOCATION_TYPE_NOUN[location.locationType ?? ''] ?? 'location';

  return location.primary ? `primary ${noun}` : noun;
};

const findMostRecentReplacerDraft = (draft: LocationDraft, allDrafts: LocationDraft[]): LocationDraft | undefined =>
  [...allDrafts]
    .reverse()
    .find((candidate) => isSameLocationId(candidate.formData.replaceLocation?.id, draft.location.id));

export const isHiddenByImmediateReplacement = (draft: LocationDraft, allDrafts: LocationDraft[]): boolean => {
  const replacer = findMostRecentReplacerDraft(draft, allDrafts);

  return !!replacer && isImmediateEffectiveDate(replacer.formData.replaceLocation?.effectiveAt);
};

export const getLocationCardLabel = (draft: LocationDraft, allDrafts: LocationDraft[]): string | null => {
  const replacer = findMostRecentReplacerDraft(draft, allDrafts);
  const replacerEffectiveAt = replacer?.formData.replaceLocation?.effectiveAt;

  if (replacer && replacerEffectiveAt && !isImmediateEffectiveDate(replacerEffectiveAt)) {
    return `Active until ${formatEffectiveDate(replacerEffectiveAt)}`;
  }

  const { replaceLocation } = draft.formData;

  if (replaceLocation?.id && replaceLocation.effectiveAt && !isImmediateEffectiveDate(replaceLocation.effectiveAt)) {
    return `New ${getReplacementNoun(draft.location)} starting ${formatEffectiveDate(replaceLocation.effectiveAt)}`;
  }

  return draft.location.schedulingDetails ?? null;
};
